import React, { useMemo, useState } from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CloseIcon from '@material-ui/icons/Close';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import gql from 'graphql-tag';
import { Form, Field } from 'react-final-form';
import { EmailTemplateMessageTypeEnum, EmailTemplateTypeEnum } from '../EmailTemplateRespository';
import { EmailStyle } from '../tabs/NewEnquiryResponseTemplateTab';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import AttachmentsField, { AttachedFile } from '../../components/AttachmentsField';
import { client } from '../..';
import { showNotification } from '../../App';
import { MessageTypeSelector } from '../selectors/MessageTypeSelector';
import { AvailableAreaOfLawSelector } from '../selectors/AvailableAreaOfLawSelector';
import { ExternalEmailTemplateSelector } from '../selectors/ExternalEmailTemplateSelector';
import ConfirmDialog from '../../components/Dialogs/ConfirmDialog';
import { EditorWrapper } from '../../components/Editor';
import { EmailTemplate } from '../EmailTemplateContentWithSidebar';
import { Alert } from '@material-ui/lab';
import { AreaOfLawSelector } from '../../enquirySummary/selectors/AreaOfLawSelector';
import { formatNumber } from '../../helpers/ChartHelper';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILES        = 5;
const DESCRIPTION_MAX_LENGTH = 500;

// ─── Types ────────────────────────────────────────────────────────────────────

// export interface EmailTemplate {
//     id?:               string | number;
//     emailTemplateGuid?: string;
//     subject:           string;
//     description:       string;
//     body?:             string;
//     template?:         string;
//     attachments?:      AttachedFile[];
//     messageType?:      string | number;
//     name?:             string;
//     areaOfLaw?:        string;
//     externalTemplate?: string;
//     active?:           boolean;
// }

export interface ShowFields {
    attachments?:      boolean;
    messageType?:      boolean;
    name?:             boolean;
    areaOfLaw?:        boolean;
    externalTemplate?: boolean;
    active?:           boolean;
    description?:       boolean;
}

const DEFAULT_SHOW_FIELDS: Required<ShowFields> = {
    attachments:      true,
    messageType:      false,
    name:             false,
    areaOfLaw:        false,
    externalTemplate: false,
    active:           true,
    description:      true,
};

export interface EmailTemplateDialogProps {
    open:      boolean;
    onClose:   () => void;
    onSave:    (selectedTemplate: EmailTemplate | null) => Promise<void>;
    title?:    string;
    subtitle?: string;

    // ── Optional field visibility ──
    // subject / description / body are always shown.
    // Everything in showFields defaults to hidden (attachments defaults to shown)
    // unless explicitly toggled, so this dialog can be reused across different contexts.
    // e.g. showFields={{ messageType: true, name: true, areaOfLaw: true }}
    showFields?: ShowFields;
    emailType?: EmailTemplateTypeEnum;
    messageType?: EmailTemplateMessageTypeEnum;
    selectedTemplate: EmailTemplate | null;
    isCreate?: boolean;
    areaOfLawMultiple?: boolean;

    requiredAreaOfLawWarning?: boolean;
}

interface FormValues {
    subject:           string | null;
    description:       string | null;
    body:              string | null;
    attachments:       AttachedFile[];
    messageType:       IAutoCompleteItem | null;
    name:              string | null;
    areaOfLaw:         IAutoCompleteItem | IAutoCompleteItem[] | null;
    externalTemplate:  IAutoCompleteItem | null;
    active:            boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const stripHtml = (html: string): string =>
    html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

const normalizeEditorHtml = (html?: string | null): string =>
    (html ?? '')
        .replace(/\u00a0/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\sdata-mce-[^=]+="[^"]*"/gi, '')
        .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
        .replace(/>\s+</g, '><')
        .replace(/\s+/g, ' ')
        .trim();

const isEditorValueEqual = (left?: string | null, right?: string | null): boolean => {
    const leftValue = left ?? '';
    const rightValue = right ?? '';

    if (leftValue === rightValue) return true;

    const leftText = stripHtml(leftValue.replace(/\u00a0|&nbsp;/gi, ' '));
    const rightText = stripHtml(rightValue.replace(/\u00a0|&nbsp;/gi, ' '));
    if (!leftText && !rightText) return true;

    return normalizeEditorHtml(leftValue) === normalizeEditorHtml(rightValue);
};

// ─── Field-level validators ─────────────────────────────────────────────────

const validateSubject = (value?: string): string | undefined => {
    if (!value || !value.trim()) return 'Email subject is required.';
    if (value.trim().length > 255) return 'Subject must be 255 characters or fewer.';
    return undefined;
};

const validateDescription = (value?: string): string | undefined => {
    if (value && value.length > DESCRIPTION_MAX_LENGTH) {
        return `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer.`;
    }
    return undefined;
};

const validateBody = (value?: string): string | undefined => {
    if (!value || !stripHtml(value)) return 'Email body cannot be empty.';
    return undefined;
};

const validateAttachments = (value?: AttachedFile[]): string | undefined => {
    const list = (value ?? []).filter(attachment => attachment.status !== 2);
    if (list.length > MAX_FILES) return `Maximum ${MAX_FILES} files allowed.`;
    if (list.some(a => a.errors.length > 0)) return 'Remove invalid files before saving.';
    return undefined;
};

const validateName = (value?: string): string | undefined => {
    if (!value || !value.trim()) return 'Name is required.';
    if (value.trim().length > 255) return 'Name must be 255 characters or fewer.';
    return undefined;
};

const validateRequiredSelect = (label: string) => (value?: string): string | undefined => {
    if (!value) return `${label} is required.`;
    return undefined;
};

const validateMessageType = validateRequiredSelect('Message type');
// const validateAreaOfLaw    = validateRequiredSelect('Area of law');

const buildMatchedProperties = (areaOfLaw?: IAutoCompleteItem | IAutoCompleteItem[] | null) => {
    const selectedAreaOfLaw = Array.isArray(areaOfLaw)
        ? areaOfLaw
        : areaOfLaw
            ? [areaOfLaw]
            : [];

    return {
        entity: 'areaOfLaw',
        properties: selectedAreaOfLaw.length > 0
            ? selectedAreaOfLaw.map((area: IAutoCompleteItem) => ({
                key: area.value,
                scope: null,
                recipient: null,
                matterType: null,
                matterCategory: null,
            }))
            : [{
                key: null,
                scope: null,
                recipient: null,
                matterType: null,
                matterCategory: null,
            }],
    };
};

const mapAttachmentsToUploadFiles = (attachments?: AttachedFile[]) =>
    (attachments ?? [])
        .filter((attachment: AttachedFile) => attachment.guidID != null || attachment.status !== 2)
        .map((attachment: AttachedFile) => ({
            file: attachment.file,
            name: attachment.name,
            guidID: attachment.guidID ?? null,
            status: attachment.status ?? 0,
        }));

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialog: {
            '& .MuiDialog-paper': {
                // maxWidth: 860,
                // width: '100%',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'hidden',
            },
        },

        dialogTitle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(2, 2.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
        },

        titleText: {
            fontSize: 15,
            fontWeight: 700,
            color: theme.palette.text.primary,
        },

        titleSub: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginTop: 2,
        },

        closeButton: {
            color: theme.palette.text.secondary,
            padding: 6,
        },

        dialogContent: {
            padding: theme.spacing(2.5),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            overflowY: 'auto',
            flex: 1,
        },

        sectionLabel: {
            fontSize: 11,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            marginBottom: theme.spacing(1.5),
        },

        fieldsCard: {
            backgroundColor: theme.palette.grey[50],
            borderRadius: 10,
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.75),
        },

        fieldLabel: {
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
            display: 'flex',
            alignItems: 'center',
            gap: 4,
        },

        infoIcon: {
            fontSize: 14,
            color: theme.palette.text.disabled,
            cursor: 'help',
        },

        textField: {
              backgroundColor: '#fff',
              width: '100%',
        },

        descriptionCounterRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: theme.spacing(0.75),
            gap: theme.spacing(1),
        },

        descriptionProgressWrap: {
            flex: 1,
            '& .MuiLinearProgress-colorPrimary': {
                backgroundColor: theme.palette.grey[300],
            }
        },

        descriptionProgress: {
            height: 6,
            borderRadius: 999,
        },

        descriptionCounterText: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            // minWidth: 110,
            textAlign: 'right',
        },

        descriptionCounterTextError: {
            color: theme.palette.error.main,
        },

        editorSection: {
            display: 'flex',
            flexDirection: 'column',
        },

        editorHint: {
            fontSize: 11,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.75),
            fontStyle: 'italic',
        },

        editorError: {
            fontSize: 11,
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.75),
        },

        editorWrapper: {
            borderRadius: 8,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            '& .tox-tinymce': {
                border: 'none !important',
            },
        },

        editorWrapperError: {
            borderColor: theme.palette.error.main,
        },

        dialogActions: {
            padding: theme.spacing(1.5, 2.5),
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: theme.spacing(1),
            flexShrink: 0,
        },

        cancelBtn: {
            fontSize: 13,
            textTransform: 'none',
            color: theme.palette.text.secondary,
        },

        saveBtn: {
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            paddingLeft: theme.spacing(2.5),
            paddingRight: theme.spacing(2.5),
            '&:hover': { boxShadow: 'none' },
        },
    })
);

interface UpdateEmailTemplateMutationData {
    updateEmailTemplate: {
        status: boolean;
        error?: string | null;
    };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EmailTemplateDialog: React.FC<EmailTemplateDialogProps> = ({
    open,
    onClose,
    onSave,
    title,
    subtitle,
    showFields,
    emailType = EmailTemplateTypeEnum.MATTER_MARKETING_EMAIL,
    messageType = EmailTemplateMessageTypeEnum.EMAIL,
    selectedTemplate: template,
    isCreate = true,
    areaOfLawMultiple = true,
    requiredAreaOfLawWarning = false,
}) => {
    const classes = useStyles();
    const isEdit  = Boolean(template?.emailTemplateGuid);

    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

    const fields = { ...DEFAULT_SHOW_FIELDS, ...showFields };
    const showAttachments      = fields.attachments;
    const showMessageType      = fields.messageType;
    const showName             = fields.name;
    const showAreaOfLaw        = fields.areaOfLaw;
    const showExternalTemplate = fields.externalTemplate;
    const showActive           = fields.active;
    const showDescription      = fields.description;

    const resolvedTitle = title ?? (isEdit ? 'Edit Template' : 'New Email Template');
    const resolvedSubtitle = subtitle ?? (
        isEdit
            ? 'Update the subject, description or body of this template'
            : 'Create a matter marketing email template'
    );

    const matchedPropertys: IAutoCompleteItem[] = template?.matchedPropertys?.matchedProperty
        .filter((property) => property.key)
        .map((property) => ({
            label: property.keyDescription,
            value: property.key,
        })) ?? [];

    const initialValues: FormValues = useMemo(() => {
        return {
            subject:          template?.subject          ?? '',
            description:      template?.description      ?? '',
            body:             template?.template ?? '',
            attachments:      (template?.files ?? []).map(file => ({
                uid: file.guidID ?? file.name,
                file: null,
                name: file.name,
                url: file.url,
                guidID: file.guidID,
                status: 0,
                errors: [],
            })),
            messageType:  !template?.messageType ? { label: 'Email', value: EmailTemplateMessageTypeEnum.EMAIL } : { label: 'SMS', value: EmailTemplateMessageTypeEnum.SMS },
            name:             template?.name              ?? '',
            areaOfLaw:    !isCreate ?  areaOfLawMultiple ? (matchedPropertys.length > 0 ? matchedPropertys : null) : (matchedPropertys[0] ?? null) : null,
            // Explicit bug report: emailStyle (used below in handleSubmit) is read off
            // values.externalTemplate, not its own field - without emailStyle included here,
            // editing an existing styled (VISION_SIX/ADAPTIVE_CARD) template without touching
            // this dropdown left it undefined, silently resetting the template to HTML on save.
            externalTemplate: template?.integrationTemplateName && template.integrationTemplateId
                ? { label: template.integrationTemplateName, value: template.integrationTemplateId, emailStyle: template.emailStyle ?? null }
                : null,
            active:           template?.isActive             ?? true,
        };
    }, [areaOfLawMultiple, isCreate, matchedPropertys, template]);

    const editorKey = open
        ? `${String(template?.emailTemplateGuid ?? 'new')}-${isCreate ? 'create' : 'edit'}-${areaOfLawMultiple ? 'multi' : 'single'}`
        : 'closed';

    // const validate = (values: FormValues) => ({
    //     subject:          validateSubject(values.subject),
    //     description:      validateDescription(values.description),
    //     body:             validateBody(values.body),
    //     attachments:      showAttachments ? validateAttachments(values.attachments) : undefined,
    //     name:             showName ? validateName(values.name) : undefined,
    //     messageType:      showMessageType ? validateMessageType(values.messageType) : undefined,
    //     areaOfLaw:        showAreaOfLaw ? validateAreaOfLaw(values.areaOfLaw) : undefined,
    //     externalTemplate: undefined,
    // });

    const handleSubmit = async (values: FormValues) => {
        const mutationResult = await client.mutate<UpdateEmailTemplateMutationData>({
            mutation: UpdateEmailTemplate,
            variables: {
                isInternalRole: template?.isInternalRole ?? null,
                roleId: template?.roleGuid ?? null,
                selectedUserId: template?.userGuid ?? null,
                template: values.body ?? null,
                emailType: EmailTemplateTypeEnum[emailType],
                subject: values.subject && values.subject.trim().length > 0 ? values.subject.trim() : null,
                matchedProperties: buildMatchedProperties(values.areaOfLaw),
                integrationTemplateId: values.externalTemplate?.value ?? null,
                // Explicit bug report: emailStyle is a GraphQL enum scalar, which must be sent
                // as the member's string name (e.g. "VISION_SIX"), not its numeric TS enum
                // value (e.g. 2) - sending the raw number failed with "Invalid Scalar value".
                // Same EmailStyle[...] reverse-mapping conversion every sibling dialog
                // (ClientAndContactTemplateDialog.tsx etc.) already applies before this same
                // mutation call.
                emailStyle: values.externalTemplate?.emailStyle != null
                    ? EmailStyle[values.externalTemplate.emailStyle]
                    : EmailStyle[EmailStyle.HTML],
                emailTemplateGuid: isCreate ? null : template?.emailTemplateGuid ?? null,
                description: values.description && values.description.trim().length > 0 ? values.description : null,
                name: values.name && values.name.trim().length > 0 ? values.name.trim() : null,
                isActive: values.active,
                files: values.attachments.length > 0 ? mapAttachmentsToUploadFiles(values.attachments) : null,
                messageType: values.messageType?.value ?? messageType,
            },
        });

        const result = mutationResult.data?.updateEmailTemplate;

        if (!result || result.error) {
            throw new Error(result?.error ?? 'Failed to save email template.');
        }

        showNotification(null, 'Successfully submitted', 'info');
        await onSave(template ?? null);
        onClose();
    };

    if (!open) return null;

    return (
        <Form<FormValues>
            key={editorKey}
            initialValues={initialValues}
            subscription={{ submitting: true, pristine: true, values: true }}
            keepDirtyOnReinitialize={true}
            initialValuesEqual={() => true}
            // validate={validate}
            onSubmit={async (values: FormValues) => {
                try {
                    await handleSubmit(values);
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    showNotification('Failed to save email template', errorMessage, 'error');
                }
            }}
            render={({ handleSubmit: submitForm, submitting, dirty, pristine, values }) => {
                
                // const canSave = !submitting && (!invalid || (submitFailed && dirtySinceLastSubmit));

                const canSave = !pristine && !submitting;

                // Explicit request: matches ClientAndContactTemplateDialog.tsx's own
                // isExternalTemplate (emailStyle > 1 - VISION_SIX specifically, not
                // ADAPTIVE_CARD) - that style's real content lives in the external system, not
                // this app, so Subject/Body become a read-only mirror of it rather than an
                // editable draft that would silently go unused.
                const isExternalTemplate = (values.externalTemplate?.emailStyle ?? 0) > 1;

                // Gate every "close" trigger (backdrop click, Escape, X button, Cancel
                // button) behind a confirmation when the form has unsaved changes.
                const requestClose = () => {
                    if (!pristine) {
                        setConfirmCloseOpen(true);
                    } else {
                        onClose();
                    }
                };

                return (
                    <>
                    <Dialog 
                        open={open} 
                        onClose={requestClose} 
                        className={classes.dialog} 
                        fullWidth={true}
                        maxWidth="lg"
                        disableEnforceFocus
                    >

                        {/* ── Title ── */}
                        <div className={classes.dialogTitle}>
                            <div>
                                <Typography className={classes.titleText}>
                                    {resolvedTitle}
                                </Typography>
                                {!!resolvedSubtitle && (
                                    <Typography className={classes.titleSub}>
                                        {resolvedSubtitle}
                                    </Typography>
                                )}
                            </div>
                            <IconButton className={classes.closeButton} size="small" onClick={requestClose}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* ── Content ── */}
                        <form onSubmit={submitForm} noValidate style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                            <DialogContent className={classes.dialogContent} dividers={false}>

                                {/* Subject + Description */}
                                <div>
                                    {requiredAreaOfLawWarning && showAreaOfLaw && !(
                                        Array.isArray(values.areaOfLaw)
                                            ? values.areaOfLaw.length > 0
                                            : Boolean(values.areaOfLaw)
                                    ) && (
                                        <Alert 
                                            severity="warning"
                                            style={{ marginBottom: 16 }}
                                        >        
                                            There can only be one template for all areas of law.
                                        </Alert>
                                    )}

                                    <Typography className={classes.sectionLabel}>Template Details</Typography>
                                    <div className={classes.fieldsCard}>
                                        <Grid container spacing={2}>

                                            {showMessageType && (
                                                <Grid item xs={12} sm={6}>
                                                    <Field name="messageType" validate={validateMessageType}>
                                                        {({ input, meta }) => {
                                                            return (
                                                            <MessageTypeSelector 
                                                                {...input}
                                                                value={input.value || null}
                                                                label="Message Type"
                                                                name="messageType"
                                                                disablePortal={false}
                                                                className={classes.textField}
                                                                error={meta.error && meta.touched}
                                                                required={true}
                                                                helperText={
                                                                    meta.error &&
                                                                    meta.touched
                                                                        ? 'Message Type is required'
                                                                        : ''
                                                                }
                                                                onBlur={input.onBlur}
                                                                onSelection={(selection: IAutoCompleteItem) => {
                                                                    input.onChange(selection)
                                                                }}
                                                                variant='outlined'
                                                                size='small'
                                                            />
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {showName && (
                                                <Grid item xs={12} sm={6}>
                                                    <Field name="name" validate={validateName}>
                                                        {({ input, meta }) => (
                                                            <TextField
                                                                label="Name"
                                                                required={true}
                                                                className={classes.textField}
                                                                variant="outlined"
                                                                fullWidth
                                                                placeholder="e.g. New Client Welcome"
                                                                inputProps={{ maxLength: 255 }}
                                                                error={meta.touched && Boolean(meta.error)}
                                                                helperText={meta.touched && meta.error ? meta.error : ''}
                                                                size="small"
                                                                {...input}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {showAreaOfLaw && (
                                                <Grid item xs={12} sm={6}>
                                                    <Field 
                                                        name="areaOfLaw" 
                                                        // validate={validateAreaOfLaw}
                                                    >
                                                        {({ input, meta }) => {
                                                            const areaOfLawValue = areaOfLawMultiple
                                                                ? (Array.isArray(input.value) ? input.value : input.value ? [input.value] : [])
                                                                : (Array.isArray(input.value) ? input.value[0] ?? null : input.value ?? null);

                                                            return (
                                                                areaOfLawMultiple ? (
                                                                    <AreaOfLawSelector
                                                                        {...input}
                                                                        name="areaOfLaw"
                                                                        label="Area Of Law"
                                                                        value={areaOfLawValue}
                                                                        disablePortal={false}
                                                                        error={meta.error && meta.touched}
                                                                        onBlur={input.onBlur}
                                                                        onSelection={(selection: IAutoCompleteItem | IAutoCompleteItem[], name: string) => {
                                                                            input.onChange(selection)
                                                                        }}
                                                                        helperText={
                                                                            meta.error &&
                                                                            meta.touched
                                                                                ? 'Assigned to is required'
                                                                                : ''
                                                                        }
                                                                        variant="outlined"
                                                                        size="small"
                                                                        className={classes.textField}
                                                                        multiple={true}
                                                                    />
                                                                ) : (
                                                                    <AvailableAreaOfLawSelector
                                                                        {...input}
                                                                        emailType={emailType}
                                                                        messageType={values.messageType?.value as EmailTemplateMessageTypeEnum | undefined}
                                                                        name="areaOfLaw"
                                                                        label="Area Of Law"
                                                                        value={areaOfLawValue ?? undefined}
                                                                        disablePortal={false}
                                                                        error={meta.error && meta.touched}
                                                                        onBlur={input.onBlur}
                                                                        onSelection={(selection: IAutoCompleteItem | IAutoCompleteItem[], name: string) => {
                                                                            input.onChange(selection);
                                                                        }}
                                                                        helperText={
                                                                            meta.error &&
                                                                            meta.touched
                                                                                ? 'Assigned to is required'
                                                                                : ''
                                                                        }
                                                                        variant="outlined"
                                                                        size="small"
                                                                        className={classes.textField}
                                                                    />
                                                                )
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {showExternalTemplate && values.messageType?.value === EmailTemplateMessageTypeEnum.EMAIL && (
                                                <Grid item xs={12} sm={6}>
                                                    <Field name="externalTemplate">
                                                        {({ input, meta }) => (
                                                            <ExternalEmailTemplateSelector
                                                                {...input}
                                                                name="externalTemplate"
                                                                label="External Template"
                                                                value={input.value ? input.value : undefined}
                                                                disablePortal={false}
                                                                // required={true}
                                                                error={meta.error && meta.touched}
                                                                // tslint:disable-next-line: jsx-alignment no-any
                                                                onSelection={(selection: IAutoCompleteItem | any, name: string) => {
                                                                    input.onChange(selection);
                                                                }}
                                                                helperText={
                                                                    meta.error &&
                                                                    meta.touched
                                                                        ? 'Assigned by is required'
                                                                        : ''
                                                                }
                                                                variant='outlined'
                                                                size='small'
                                                                className={classes.textField}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {values.messageType?.value === EmailTemplateMessageTypeEnum.EMAIL && (
                                                <Grid item xs={12}>
                                                    <Field name="subject" validate={validateSubject}>
                                                        {({ input, meta }) => (
                                                            <TextField
                                                                className={classes.textField}
                                                                variant="outlined"
                                                                fullWidth
                                                                label="Email Subject"
                                                                placeholder="e.g. Update on your matter – {matterTitle}"
                                                                inputProps={{ maxLength: 255 }}
                                                                autoFocus
                                                                error={meta.touched && Boolean(meta.error)}
                                                                helperText={meta.touched && meta.error ? meta.error : ''}
                                                                size="small"
                                                                InputProps={{ readOnly: isExternalTemplate }}
                                                                {...input}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {showDescription && (
                                                <Grid item xs={12}>
                                                    <Field name="description" validate={validateDescription}>
                                                        {({ input, meta }) => {
                                                            const descriptionValue = input.value ?? '';
                                                            const usedChars = descriptionValue.length;
                                                            const overBy = Math.max(usedChars - DESCRIPTION_MAX_LENGTH, 0);
                                                            const isOverLimit = overBy > 0;
                                                            // const isNearLimit = !isOverLimit && DESCRIPTION_MAX_LENGTH - usedChars <= 50;
                                                            const progressValue = Math.min((usedChars / DESCRIPTION_MAX_LENGTH) * 100, 100);
                                                            const isError = meta.touched && Boolean(meta.error);

                                                            return (
                                                                <>
                                                                    <TextField
                                                                        label="Description (optional)"
                                                                        className={classes.textField}
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        multiline
                                                                        minRows={5}
                                                                        placeholder="What is this template used for?"
                                                                        error={isError}
                                                                        helperText={isError ? meta.error : ''}
                                                                        size="small"
                                                                        {...input}
                                                                    />

                                                                    <div className={classes.descriptionCounterRow}>
                                                                        <div className={classes.descriptionProgressWrap}>
                                                                            <LinearProgress
                                                                                variant="determinate"
                                                                                value={progressValue}
                                                                                className={classes.descriptionProgress}
                                                                                color={isOverLimit ? 'secondary' : 'primary'}
                                                                            />
                                                                        </div>
                                                                        <Typography className={[classes.descriptionCounterText, isOverLimit ? classes.descriptionCounterTextError : ''].join(' ')}>
                                                                            {formatNumber(usedChars)} / {formatNumber(DESCRIPTION_MAX_LENGTH)}{isOverLimit ? ` (${overBy} over)` : ''}
                                                                        </Typography>
                                                                    </div>
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {showActive && (
                                                <Grid item xs={12}>
                                                    <Field name="active" type="checkbox">
                                                        {({ input }) => (
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        color="primary"
                                                                        checked={Boolean(input.checked)}
                                                                        onChange={input.onChange}
                                                                        name={input.name}
                                                                    />
                                                                }
                                                                label="Active"
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </div>
                                </div>

                                <Divider />

                                {/* Body editor */}
                                <div>
                                    <Typography className={classes.sectionLabel}>Email Body</Typography>
                                    <Field name="body" validate={validateBody} isEqual={isEditorValueEqual}>
                                        {({ input: { value, onChange }, meta }) => {
                                            const showError = meta.touched && meta.error;

                                            // Explicit request: matches ClientAndContactTemplateDialog.tsx's own
                                            // isExternalTemplate branch - VISION_SIX's real content lives in the
                                            // external system, so this is a read-only mirror of it, not an
                                            // editable draft.
                                            if (isExternalTemplate) {
                                                return (
                                                    <div
                                                        className={classes.editorSection}
                                                        dangerouslySetInnerHTML={{ __html: value || '' }}
                                                    />
                                                );
                                            }

                                            return (
                                                <div className={classes.editorSection}>
                                                    <div className={[classes.editorWrapper, showError ? classes.editorWrapperError : ''].join(' ')}>
                                                        <EditorWrapper
                                                            key={editorKey}
                                                            value={value || ''}
                                                            onEditorChange={onChange}
                                                            emailType={emailType}
                                                            messageType={(values.messageType?.value as EmailTemplateMessageTypeEnum | undefined) ?? messageType}
                                                        />
                                                    </div>
                                                    {showError ? (
                                                        <Typography className={classes.editorError}>{meta.error}</Typography>
                                                    ) : (
                                                        <Typography className={classes.editorHint}>
                                                            Type {'{'} in the editor to browse all available merge fields
                                                        </Typography>
                                                    )}
                                                </div>
                                            );
                                        }}
                                    </Field>
                                </div>

                                {values.messageType?.value !== EmailTemplateMessageTypeEnum.SMS && showAttachments && (
                                    <>
                                        <Divider />

                                        {/* ── Attachments ── */}
                                        <div>
                                            <Typography className={classes.sectionLabel}>Attachments</Typography>
                                            <Field name="attachments" validate={validateAttachments}>
                                                {(fieldProps) => (
                                                    <AttachmentsField 
                                                        {...fieldProps} 
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </>
                                )}

                            </DialogContent>

                            {/* ── Actions ── */}
                            <DialogActions className={classes.dialogActions} disableSpacing>
                                <Button
                                    // className={classes.saveBtn}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={!canSave}
                                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
                                >
                                    {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Template'}
                                </Button>
                                <Button 
                                    className={classes.cancelBtn} 
                                    onClick={requestClose} 
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                            </DialogActions>
                        </form>

                    </Dialog>

                    <ConfirmDialog
                        open={confirmCloseOpen}
                        title="Discard unsaved changes?"
                        message="You have changes that haven't been saved yet. If you close now, those changes will be lost."
                        confirmLabel="Discard changes"
                        cancelLabel="Keep editing"
                        onCancel={() => setConfirmCloseOpen(false)}
                        onConfirm={() => {
                            setConfirmCloseOpen(false);
                            onClose();
                        }}
                    />
                    </>
                );
            }}
        />
    );
};

export default EmailTemplateDialog;

const UpdateEmailTemplate = gql`
    mutation UpdateEmailTemplate(
        $emailType: EmailType,
        $emailStyle: EmailStyle,
        $isInternalRole: Boolean,
        $roleId: String,
        $selectedUserId: String,
        $template: String,
        $subject: String,
        $matchedProperties: MatchedPropertiesInput,
        $ccAssistant: Boolean,
        $integrationTemplateId: String,
        $emailTemplateGuid: String,
        $description: String,
        $name: String,
        $isActive: Boolean,
        $files: [UploadFile],
        $messageType: MessageType
    ) {
        updateEmailTemplate(
            emailType: $emailType,
            emailStyle: $emailStyle,
            isInternalRole: $isInternalRole,
            roleId: $roleId,
            selectedUserId: $selectedUserId,
            template: $template,
            subject: $subject,
            matchedProperties: $matchedProperties,
            ccAssistant: $ccAssistant,
            integrationTemplateId: $integrationTemplateId,
            emailTemplateGuid: $emailTemplateGuid,
            description: $description,
            name: $name,
            isActive: $isActive,
            files: $files,
            messageType: $messageType
        ) {
            status
            error
        }
    }
`;